import { InjectionToken } from '@angular/core';

/** O `cardapio.json` como o CDN o entrega: o hash que o app compara e o cardápio em si. */
export interface EnvelopeDoCardapio {
    hash: string;
    cardapio: any;
}

/** O cardápio que quem renderiza já baixou, e de qual loja ele é. */
export interface CardapioDoRender {
    slug: string;
    envelope: EnvelopeDoCardapio;
}

/**
 * O cardápio entregue pronto ao render do servidor, em vez de o app baixá-lo sozinho.
 *
 * Quem provê é o `renderizarCardapio()` do `main.server.ts`, chamado pela Lambda de render e pelo
 * render de fumaça do deploy. A Lambda grava na página o ETag do `cardapio.json` que baixou, e o
 * porteiro compara esse ETag com o do S3 a cada visita para saber se a página ainda vale. Se o app
 * baixasse o cardápio de novo, uma gravação entre os dois downloads deixaria a página com o
 * conteúdo novo e o ETag velho, ou o contrário. Com o cardápio injetado, os dois vêm do mesmo
 * download.
 */
export const CARDAPIO_DO_RENDER = new InjectionToken<CardapioDoRender>('CARDAPIO_DO_RENDER');

/**
 * Campos do `cardapio.info` que NÃO vão para o estado da página do SSR.
 *
 * Nenhuma tela do app lê estes campos (conferido em 23/09/2026; o `check:campos-fora-do-estado`
 * garante que continue assim). Eles existem no `cardapio.json` para outros sistemas — o robô de
 * WhatsApp lê as mensagens dele de lá —, mas embutidos em cada página só pesavam: 1,3 a 8,5 KB
 * comprimidos por loja (2 a 13% do HTML; o mapa de áreas de entrega é o maior). E dois deles não
 * precisam viajar em página pública: a configuração de nota fiscal traz o e-mail do contador.
 *
 * 26/09/2026: entram os campos de gestão/ERP/cobrança que o levantamento de leituras (app + as libs
 * `restaurantoperations`, `cardapnator`, `cardapiocache`) achou com ZERO leituras. Só nomes
 * inconfundíveis — os genéricos (`address`, `uuid`, `cnpj`...) ficam, porque o gate não consegue
 * distinguir a leitura de outro objeto com o mesmo nome.
 *
 * ⚠️ O estado é gravado no aparelho como `cardapio.cache` com o hash REAL
 * (`restaurant.service.ts`), e as visitas CSR seguintes (até 24 h) usam essa cópia. Por isso só
 * sai daqui o que NINGUÉM lê — nunca o que alguma tela lê mais tarde (modal, checkout).
 */
export const CAMPOS_DO_INFO_FORA_DO_ESTADO: ReadonlyArray<string> = [
    'deliveryFeeZone',
    'rasaCustomMessages',
    'customStatusMessages',
    'invoice_settings',
    'is_pre_paid', 'tef_credit', 'tef_debit', 'tef_pix', 'pizza_category_ncm_id',
    'has_whatsapp_automation', 'supress_payments_warnings_till',
    'invoice_provider', 'enotas_id', 'mp_notas_id',
    'ask_cancellation_reason', 'cancellation_reason_is_mandatory', 'use_new_order_page', 'unblock_batch',
    'geocoding_provider', 'payment_overdue_at', 'motoboy_is_mandatory', 'revenue_limit',
    'use_html_templating', 'service_fee_in_reports', 'theme_id', 'rasa_configuration_id', 'uses_ip_block',
    'weight_scale_port', 'internal_message', 'show_stock_paused_items', 'can_request_help_whatsapp',
    'deferred_payment', 'has_deferred_payment_amount_limit', 'deferred_payment_amount_limit',
    'has_motoboy_remuneration', 'has_self_service_enabled', 'self_service_mode', 'barcode_protocol',
    'has_order_status_webhooks_integration', 'has_order_status_polling_integration', 'subscription_type',
    'new_payments_directive', 'new_menu_editor', 'old_menu_editor', 'rrweb_recording_enabled',
    'digital_certificate_expiration', 'csm_id', 'has_order_webhook_on_dynamo', 'cs_client_objective',
    'plan_id', 'secondary_plan_id', 'has_entrega_facil', 'need_ask_nps', 'has_new_checkout_interface',
    'tuna_condition_id', 'tuna_merchant_id', 'order_late_cutoff_delivery', 'order_late_cutoff_balcony',
    'new_order_tag_mode', 'send_integration_orders_webhook', 'invoicesShouldConsiderDeliveryFee',
    'has_fb_page_token', 'mapsProvider', 'highlight_images', 'active_theme', 'rasa_configuration',
    'message_channel_settings',
];

/**
 * Campos do `cardapio.menu` que NÃO vão para o estado, por nível (26/09/2026).
 *
 * Códigos de ERP/nota fiscal e carimbos de data que o `cardapio.json` traz para outros sistemas.
 * Zero leituras no app e nas libs — o `check:campos-fora-do-estado` confere o app; as libs foram
 * conferidas à mão (nenhum destes nomes aparece em `cardapnator`, `restaurantoperations` ou
 * `cardapiocache`). Nomes que o app usa para OUTRA coisa (`createdAt` de pedido, `extraID` como
 * variável local, `product_id` do carrinho) também estão aqui: o gate só reprova leitura dos
 * nomes inconfundíveis (`CAMPOS_DO_MENU_VIGIADOS`).
 *
 * NÃO entram (alguém lê, mesmo que só a lib): `templateID`, `stock_id`, `technicalSheet`,
 * `deliveryPriceEnabled`, `tablePrice*`, `unavailable*`, `*Exceptions`, `hoursTemplates`, `type`.
 */
export const CAMPOS_DO_MENU_FORA_DO_ESTADO = {
    categoria: ['ncmID', 'cuisine', 'assortment', 'isSelfService'],
    produto: ['cuisine', 'ncmID', 'externalID', 'assortment', 'selfService', 'allYouCanEatPrice', 'productCode',
        'productCodeID', 'barcode', 'pdvCode', 'totvs_id'],
    grupoDeComplementos: ['externalID', 'createdAt', 'updatedAt', 'assortment', 'isGlobal', 'product_id', 'pdvCode'],
    opcao: ['ncmID', 'invoicePrice', 'extraID', 'externalID', 'createdAt', 'updatedAt', 'barcode', 'pdvCode',
        'totvs_id', 'assortment'],
    tamanhoDePizza: ['isDraft', 'cuisine', 'ncmID', 'externalID', 'assortment'],
    combo: ['assortment', 'externalID'],
    menu: ['categoryAssorts'],
} as const;

/** Nomes do menu que só existem no cardápio — o gate reprova qualquer leitura deles no app. */
export const CAMPOS_DO_MENU_VIGIADOS: ReadonlyArray<string> = [
    'ncmID', 'allYouCanEatPrice', 'productCode', 'productCodeID', 'barcode', 'pdvCode', 'totvs_id', 'invoicePrice',
    'isGlobal', 'isDraft', 'categoryAssorts',
];

function sem(obj: any, campos: ReadonlyArray<string>): any {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const copia = { ...obj };
    for (const c of campos) delete copia[c];
    return copia;
}

function cadaUm(lista: any, fn: (x: any) => any): any {
    return Array.isArray(lista) ? lista.map(fn) : lista;
}

/** O menu sem os campos de `CAMPOS_DO_MENU_FORA_DO_ESTADO`, em cópias — o original não muda. */
export function menuParaOEstado(menu: any): any {
    if (!menu || typeof menu !== 'object') return menu;
    const F = CAMPOS_DO_MENU_FORA_DO_ESTADO;
    const novo = sem(menu, F.menu);
    if (Array.isArray(menu.general)) {
        novo.general = menu.general.map((cat: any) => {
            const c = sem(cat, F.categoria);
            if (c && Array.isArray(cat.products)) c.products = cadaUm(cat.products, (p) => sem(p, F.produto));
            return c;
        });
    }
    if (Array.isArray(menu.extras)) {
        novo.extras = menu.extras.map((g: any) => {
            const c = sem(g, F.grupoDeComplementos);
            if (c && Array.isArray(g.options)) c.options = cadaUm(g.options, (o) => sem(o, F.opcao));
            return c;
        });
    }
    if (menu.pizzas && typeof menu.pizzas === 'object' && Array.isArray(menu.pizzas.sizes)) {
        novo.pizzas = { ...menu.pizzas, sizes: menu.pizzas.sizes.map((t: any) => sem(t, F.tamanhoDePizza)) };
    }
    if (Array.isArray(menu.combos)) novo.combos = menu.combos.map((c: any) => sem(c, F.combo));
    return novo;
}

/**
 * A cópia do cardápio com que o render do servidor trabalha — a original fica para a TransferState.
 *
 * As bibliotecas que montam o cardápio alteram o objeto que recebem, no lugar: a
 * `restaurantoperations` grava `info.is_closed`/`blocked`/`shift_has_*` e o `cardapnator` troca
 * `menu.general`, filtra combos, tamanhos e sabores. A TransferState só serializa no FIM do render,
 * então sem esta cópia a página levava o cardápio JÁ processado no relógio do servidor. E o
 * `is_closed` não se desfaz no navegador: a `restaurantoperations` lê o mesmo campo como
 * "fechamento manual" e devolve fechada sem olhar a hora. Foi o OP-1233 (23/09/2026): servidor em
 * UTC, lojas "fechadas" nas últimas 3 h do turno, em toda página do SSR.
 *
 * Cópia por JSON de propósito: o cardápio chega de um `cardapio.json`, então ela é exata; ~2 ms num
 * cardápio de 184 KB.
 */
export function cardapioParaORender<T>(cardapio: T): T {
    return JSON.parse(JSON.stringify(cardapio));
}

/**
 * O envelope que vai para a TransferState: o mesmo `hash` e o mesmo cardápio, sem os campos acima
 * (do `info` e do `menu`).
 *
 * Devolve uma CÓPIA rasa — o objeto original segue inteiro para quem renderiza. O `hash` fica o
 * mesmo de propósito: ele identifica a VERSÃO do cardápio (a checagem de frescor e o porteiro
 * comparam versão), e o cardápio embutido é essa versão menos campos que ninguém lê.
 */
export function envelopeParaOEstado(envelope: EnvelopeDoCardapio): EnvelopeDoCardapio {
    const cardapio = envelope && envelope.cardapio;
    if (!cardapio || !cardapio.info) return envelope;
    const novo = { ...cardapio, info: sem(cardapio.info, CAMPOS_DO_INFO_FORA_DO_ESTADO) };
    if (cardapio.menu) novo.menu = menuParaOEstado(cardapio.menu);
    return { ...envelope, cardapio: novo };
}
